import { useRef } from "react";
import {
  Typography,
  Box,
  Button,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Chip,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useMedia } from "../context/MediaContext";
import { useTheme } from "@mui/material";

const mainColor = "#635BFF";
const lightColor = "rgba(99, 91, 255, 0.1)";

type Method = Record<string, string>;

interface ChoosingMethodsProps {
  selectedMethod: string[];
  forwardMethods: string[];
  setForwardMethods: React.Dispatch<React.SetStateAction<string[]>>;
  methods: Method;
}

const ChoosingMethods: React.FC<ChoosingMethodsProps> = ({
  forwardMethods,
  setForwardMethods,
  methods,
}) => {
  const selectRef = useRef<HTMLDivElement | null>(null);
  const { isMobile600, isTablet } = useMedia();
  const theme = useTheme();
  const methodKeys = Object.keys(methods);

  return (
    <Box width={isTablet ? "100%" : "35%"} margin={isTablet ? "0 auto" : 0}>
      <FormControl
        fullWidth
        sx={{
          position: "relative",
        }}
      >
        <InputLabel
          shrink={false}
          sx={{
            position: "absolute",
            top: "40%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            pointerEvents: "none",
            fontSize: isMobile600 ? "16px" : "18px",
            zIndex: 1,
            display: forwardMethods.length === 0 ? "block" : "none",
          }}
        >
          Выберите методы
        </InputLabel>

        <Select
          ref={selectRef}
          multiple
          value={forwardMethods}
          onChange={(e) => {
            const value = e.target.value;
            setForwardMethods(
              typeof value === "string" ? value.split(",") : value,
            );
          }}
          MenuProps={{
            disableScrollLock: true,
            slotProps: {
              paper: {
                sx: {
                  borderRadius: "12px",
                  boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
                  overflow: "auto",

                  scrollbarWidth: "none",
                  "&::-webkit-scrollbar": {
                    width: 0,
                    height: 0,
                  },
                },
              },
            },
          }}
          sx={{
            width: "100%",
            maxWidth: isTablet ? "100%" : "440px",
            minWidth: 0,

            borderRadius: "12px",
            boxSizing: "border-box",
            minHeight: "81px",
            overflow: "hidden",
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor:
                forwardMethods.length !== 0 ? mainColor : "rgba(0,0,0,0.2)",
            },
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: mainColor,
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: mainColor,
              borderWidth: "2px",
            },
          }}
          renderValue={(selected) => (
            <Box
              sx={{
                display: "flex",
                gap: 0.5,
                overflowX: "auto",
                whiteSpace: "nowrap",
                scrollbarHeight: "thin",

                "&::-webkit-scrollbar": { display: "none" },
              }}
            >
              {selected.map((method) => (
                <Chip
                  key={method}
                  label={method}
                  sx={{
                    backgroundColor: mainColor,
                    color: "white",
                    fontWeight: 600,
                    borderRadius: "12px",
                    flexShrink: 0,
                  }}
                />
              ))}
            </Box>
          )}
        >
          {methodKeys.map((method) => (
            <MenuItem
              key={method}
              value={method}
              sx={{
                p: 1.5,
                display: "flex",
                alignItems: "center",
                gap: 1,
                backgroundColor: forwardMethods.includes(method)
                  ? lightColor
                  : "transparent",
                "&:hover": { backgroundColor: lightColor },
              }}
            >
              <CheckCircleIcon
                color={forwardMethods.includes(method) ? "primary" : "disabled"}
              />
              <Typography fontSize={isMobile600 ? "14px" : "16px"}>
                {method}
              </Typography>
            </MenuItem>
          ))}
        </Select>

        <Button
          onClick={() => {
            if (forwardMethods.length === methodKeys.length) {
              setForwardMethods([]);
            } else {
              setForwardMethods(methodKeys);
            }
          }}
          variant="outlined"
          size="small"
          sx={{
            backgroundColor: "rgba(0, 0, 0, 0.08)",
            borderRadius: "20px",
            width: "100%",
            textTransform: "none",
            fontSize: "0.8rem",
            px: 2,
            color: theme.palette.text.primary,

            borderColor: "divider",
            "&:hover": {
              color: theme.palette.primary.main,
              borderColor: theme.palette.primary.main,
            },
          }}
        >
          {forwardMethods.length === methodKeys.length
            ? "Убрать все"
            : "Выбрать все"}
        </Button>
      </FormControl>
    </Box>
  );
};

export default ChoosingMethods;
